'use client';

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["component", "color", "children", "buttonSize", "semanticColor", "size", "hover", "active", "focus", "iconOnly", "iconLeading", "iconTrailing", "aria-label", "aria-labelledby", "aria-hidden", "condensed", "condensedAll", "className", "as"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useContext } from 'react';
import { BrowserDefaultFocusStyleContext } from "../../contexts/BrowserDefaultFocusStyleContext.mjs";
import { ENCORE_DATA_IDS } from "../../dataEncoreIds.mjs";
import { buttonSizesWithDeprecationWarning, colorClass, controlIconSize, getButtonFont, getIconPlacement, getNextControlSize, textClass } from "../../styles/index.mjs";
import { ecx } from "../../utils.mjs";
import { useDefaultProps } from "../../hooks/useDefaultProps.mjs";
import { warn } from "../../logging.mjs";
import { ButtonChildren } from "../../styles/mixins/ButtonChildren/index.mjs";
export var ButtonComponent = /*#__PURE__*/React.forwardRef(function ButtonComponent(inProps, ref) {
  var propsWithProviderDefaults = useDefaultProps({
    props: inProps,
    name: 'ButtonTertiary'
  });
  var component = propsWithProviderDefaults.component,
    color = propsWithProviderDefaults.color,
    children = propsWithProviderDefaults.children,
    buttonSize = propsWithProviderDefaults.buttonSize,
    _propsWithProviderDef = propsWithProviderDefaults.semanticColor,
    semanticColor = _propsWithProviderDef === void 0 ? 'textSubdued' : _propsWithProviderDef,
    _propsWithProviderDef2 = propsWithProviderDefaults.size,
    size = _propsWithProviderDef2 === void 0 ? 'medium' : _propsWithProviderDef2,
    hover = propsWithProviderDefaults.hover,
    active = propsWithProviderDefaults.active,
    focus = propsWithProviderDefaults.focus,
    iconOnly = propsWithProviderDefaults.iconOnly,
    iconLeading = propsWithProviderDefaults.iconLeading,
    iconTrailing = propsWithProviderDefaults.iconTrailing,
    ariaLabel = propsWithProviderDefaults['aria-label'],
    ariaLabelledby = propsWithProviderDefaults['aria-labelledby'],
    ariaHidden = propsWithProviderDefaults['aria-hidden'],
    condensed = propsWithProviderDefaults.condensed,
    condensedAll = propsWithProviderDefaults.condensedAll,
    className = propsWithProviderDefaults.className,
    as = propsWithProviderDefaults.as,
    props = _objectWithoutProperties(propsWithProviderDefaults, _excluded);
  if (iconOnly && !ariaLabel && !ariaLabelledby && !ariaHidden) {
    warn('ButtonTertiary must have an aria-label or aria-labelledby attribute defined when using the iconOnly prop in order to be accessible.');
  }
  var _useContext = useContext(BrowserDefaultFocusStyleContext),
    useBrowserDefaultFocusStyle = _useContext.useBrowserDefaultFocusStyle;
  var rel = props.href && props.target === '_blank' ? 'noopener noreferrer' : null;

  // TODO: Remove this once buttonSize is archived, and use size instead
  var normalizedSize = buttonSize ? getNextControlSize(buttonSize) : size;
  var iconPlacement = getIconPlacement({
    iconOnly: iconOnly,
    iconLeading: iconLeading,
    iconTrailing: iconTrailing
  });
  var iconSize = iconOnly && normalizedSize === 'large' ? 'large' : controlIconSize[normalizedSize];
  var defaultElement = props.href ? 'a' : 'button';
  var polymorphicProp = as ? as : component;
  var Element = polymorphicProp ? polymorphicProp : defaultElement;
  return /*#__PURE__*/React.createElement(Element, Object.assign({}, props, {
    className: ecx(['button', 'button-tertiary', 'overflow-wrap-anywhere', 'baseline', !iconOnly && textClass(getButtonFont(normalizedSize)), _defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty({
      'button--hover': hover,
      'button--active': active,
      'focus-border': focus || !useBrowserDefaultFocusStyle,
      'focus-border--focus': focus
    }, "button--".concat(normalizedSize), !condensedAll && !iconOnly), "button-tertiary--".concat(normalizedSize), !condensedAll && !iconOnly), "button--".concat(iconPlacement), iconPlacement !== 'none' && !iconOnly), "button-tertiary--icon-only-".concat(normalizedSize), !!iconOnly), 'button-tertiary--icon-only', !!iconOnly), 'button-tertiary--condensed', condensed), 'button-tertiary--condensed-all', condensedAll), 'button-tertiary--text-subdued', semanticColor === 'textSubdued'), colorClass(semanticColor)], className),
    rel: rel,
    ref: ref,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    "aria-hidden": ariaHidden,
    "data-encore-id": ENCORE_DATA_IDS.ButtonTertiary
  }), /*#__PURE__*/React.createElement(ButtonChildren, {
    iconOnly: iconOnly,
    iconLeading: iconLeading,
    iconTrailing: iconTrailing,
    iconSize: iconSize,
    children: children
  }));
});

/**
 * TODO: Remove when we deprecate `buttonSize` helpers like `ButtonTertiary.sm`.
 */
var buttonHelpers = _objectSpread(_objectSpread({}, buttonSizesWithDeprecationWarning), {}, {
  displayName: 'ButtonTertiary'
});

/**
 * Tertiary buttons are text and icon-only buttons. These are used for the least important actions.
 *
 * [Storybook](https://encore-web.spotify.net/?path=/docs/components-buttonsecondary--docs) |
 * [Encore site](https://encore.spotify.net/web/components/buttons#tertiary) |
 * [Github](https://ghe.spotify.net/encore/web/tree/master/src/core/components/ButtonTertiary) |
 * [Figma](https://www.figma.com/design/bnQXqGkLeiTlbk8JXGDcsX/%E2%9A%99%EF%B8%8F-%E2%80%8A%E2%80%8BEncore-Web-Components?m=auto&node-id=32403-19609&t=H9FWVKuV1aX5wYth-1)
 */
export var ButtonTertiary = Object.assign(ButtonComponent, buttonHelpers);